import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  Pencil,
  // Trash2,
  RefreshCw,
  Mail,
  Loader2,
  RotateCcw,
  Eye,
  Copy,
  Check,
} from 'lucide-react';
import {
  TeamMembersWrapper,
  TeamMembersContainer,
  PageHeader,
  HeaderLeft,
  Title,
  Description,
  InviteUserButton,
  TableCard,
  TableToolbar,
  SearchSlot,
  ClientNameText,
  ClientDescriptionText,
  ClientCodeText,
  RowActionsGroup,
  RowActionButton,
  ClientLogoBox,
  ClientLogoImg,
  ClientLogoFallback,
  ClientCodeCell,
} from './Clients.styles';

import SearchBar from '../AdminTickets/components/SearchBar/SearchBar';
import { Table } from '../../../components/uielements/table';
import {
  ActionButtons,
  OutlineButon,
  EmptyState,
  RetryButton,
  SpinWrapper,
} from '../../../settings/appStyle/global.style';
import {
  useAdminClientsQuery,
  // useDeleteClient,
  useRegenerateClientCode,
} from './hooks/useClientsAdmin';
import CreateAndUpdateClientModal from './modals/CreateAndUpdateClientModal';
import InviteClientModal from './modals/InviteClientModal';

const PAGE_SIZE = 10;

const getIsDark = () => localStorage.getItem('theme') === 'dark';

const ClientLogo = ({ client }) => {
  const [failed, setFailed] = useState(false);
  const [isDark, setIsDark] = useState(getIsDark);

  useEffect(() => {
    const sync = () => setIsDark(getIsDark());
    window.addEventListener('themechange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('themechange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const src = isDark ? client.darkModeLogo?.url : client.lightModeLogo?.url;

  useEffect(() => setFailed(false), [src]);

  return (
    <ClientLogoBox>
      {src && !failed ? (
        <ClientLogoImg
          src={src}
          alt={`${client.name} logo`}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <ClientLogoFallback aria-hidden="true">
          {(client.name || '?').trim().charAt(0).toUpperCase()}
        </ClientLogoFallback>
      )}
    </ClientLogoBox>
  );
};

const Clients = () => {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const [inviteTarget, setInviteTarget] = useState(null);

  const [actioningId, setActioningId] = useState(null);
  const [actioningType, setActioningType] = useState(null);

  const [copiedId, setCopiedId] = useState(null);

  const { data, isLoading, isError, refetch, isFetching } = useAdminClientsQuery({
    page: page,
    pageSize: PAGE_SIZE,
  });

  // Delete disabled
  // const deleteClientMutation = useDeleteClient({
  //   onSuccess: () => {
  //     setActioningId(null);
  //     setActioningType(null);
  //   },
  //   onError: (err) => {
  //     setActioningId(null);
  //     setActioningType(null);
  //     window.alert(`Failed to delete client${err?.message ? `: ${err.message}` : '.'}`);
  //   },
  // });

  const regenerateCodeMutation = useRegenerateClientCode({
    onSuccess: () => {
      setActioningId(null);
      setActioningType(null);
    },
    onError: (err) => {
      setActioningId(null);
      setActioningType(null);
      window.alert(`Failed to regenerate client code${err?.message ? `: ${err.message}` : '.'}`);
    },
  });

  const clients = data?.clients ?? [];
  const total = data?.totalClients ?? 0;

  const filteredClients = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return clients;
    return clients.filter(
      (client) =>
        (client.name || '').toLowerCase().includes(query) ||
        (client.description || '').toLowerCase().includes(query) ||
        (client.clientCode || '').toLowerCase().includes(query)
    );
  }, [clients, searchValue]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(0);
  };

  const handleDashboard = () => navigate('/');
  const handleRowClick = (client) => navigate(`/users/${client._id}`);

  const handleAddClient = () => {
    setEditingClient(null);
    setIsClientModalOpen(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setIsClientModalOpen(true);
  };

  const handleCloseClientModal = () => {
    setIsClientModalOpen(false);
    setEditingClient(null);
  };

  // Delete disabled
  // const handleDeleteClient = (client) => {
  //   const confirmed =
  //     window.confirm(`Are you sure you want to delete '${client?.name}'? This action cannot be undone.
  // `);
  //   if (!confirmed) return;
  //
  //   setActioningId(client._id);
  //   setActioningType('delete');
  //   deleteClientMutation.mutate({ clientId: client._id });
  // };

  const handleRegenerateCode = (client) => {
    const confirmed = window.confirm(
      `Regenerate the client code for '${client?.name}'? The old code will stop working.`
    );
    if (!confirmed) return;

    setActioningId(client._id);
    setActioningType('regenerate');
    regenerateCodeMutation.mutate({ clientId: client._id });
  };

  const handleCopyCode = async (client) => {
    if (!client?.clientCode) return;
    try {
      await navigator.clipboard.writeText(client.clientCode);
      setCopiedId(client._id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      window.alert('Could not copy to clipboard. Please copy it manually.');
    }
  };

  const handleOpenInvite = (client) => {
    setInviteTarget({ clientId: client._id, name: client.name });
  };

  const handleCloseInvite = () => setInviteTarget(null);

  const columns = useMemo(
    () => [
      {
        Header: '',
        id: 'logo',
        accessor: (row) => row.darkModeLogo?.url || row.lightModeLogo?.url || '',
        width: '2%',
        disableSortBy: true,
        Cell: ({ row }) => <ClientLogo client={row.original} />,
      },
      {
        Header: 'Name',
        accessor: 'name',
        width: '27%',
        Cell: ({ value }) => <ClientNameText>{value}</ClientNameText>,
      },
      {
        Header: 'Description',
        accessor: 'description',
        width: '27%',
        Cell: ({ value }) => (
          <ClientDescriptionText title={value || undefined}>{value || '—'}</ClientDescriptionText>
        ),
      },
      {
        Header: 'Client code',
        accessor: 'clientCode',
        width: '25%',
        disableSortBy: true,
        Cell: ({ row, value }) => {
          const client = row.original;
          const isRegeneratingThis = actioningId === client._id && actioningType === 'regenerate';
          const isRowBusy = actioningId === client._id;
          const isCopied = copiedId === client._id;

          return (
            <ClientCodeCell>
              <ClientCodeText>{value || '—'}</ClientCodeText>

              {value && (
                <RowActionButton
                  type="button"
                  title={isCopied ? 'Copied' : `Copy code for ${client.name}`}
                  aria-label={`Copy client code for ${client.name}`}
                  onClick={() => handleCopyCode(client)}
                >
                  {isCopied ? <Check /> : <Copy />}
                </RowActionButton>
              )}

              <RowActionButton
                type="button"
                title={`Regenerate code for ${client.name}`}
                aria-label={`Regenerate client code for ${client.name}`}
                onClick={() => handleRegenerateCode(client)}
                disabled={isRowBusy}
              >
                {isRegeneratingThis ? <Loader2 data-spin="true" /> : <RefreshCw />}
              </RowActionButton>
            </ClientCodeCell>
          );
        },
      },
      {
        Header: 'Actions',
        accessor: '_id',
        width: '20%',
        disableSortBy: true,
        Cell: ({ row }) => {
          const client = row.original;
          // const isDeletingThis = actioningId === client._id && actioningType === 'delete';
          const isRowBusy = actioningId === client._id;

          return (
            <RowActionsGroup>
              <RowActionButton
                type="button"
                title={`Edit ${client.name}`}
                onClick={() => handleEditClient(client)}
                disabled={isRowBusy}
              >
                <Pencil />
              </RowActionButton>

              <RowActionButton
                type="button"
                title={`Invite ${client.name}`}
                onClick={() => handleOpenInvite(client)}
                disabled={isRowBusy}
              >
                <Mail />
              </RowActionButton>

              {/* Delete disabled
              <RowActionButton
                type="button"
                $danger
                title={`Delete ${client.name}`}
                onClick={() => handleDeleteClient(client)}
                disabled={isRowBusy}
              >
                {isDeletingThis ? <Loader2 data-spin="true" /> : <Trash2 />}
              </RowActionButton>
              */}

              <RowActionButton
                type="button"
                title={`View users for ${client.name}`}
                onClick={() => handleRowClick(client)}
                disabled={isRowBusy}
              >
                <Eye />
              </RowActionButton>
            </RowActionsGroup>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actioningId, actioningType, copiedId]
  );

  return (
    <TeamMembersWrapper>
      <TeamMembersContainer>
        <PageHeader>
          <HeaderLeft>
            <Title>Clients</Title>
            <Description>Organizations with access to this support workspace.</Description>
          </HeaderLeft>
          <ActionButtons>
            <OutlineButon onClick={handleDashboard} type="button">
              Dashboard
            </OutlineButon>
            <InviteUserButton type="button" onClick={handleAddClient}>
              <PlusIcon size={16} />
              Add Client
            </InviteUserButton>
          </ActionButtons>
        </PageHeader>

        <TableCard>
          <TableToolbar>
            <SearchSlot>
              <SearchBar
                value={searchValue}
                onChange={handleSearchChange}
                placeholder="Search clients by name, description or code…"
              />
            </SearchSlot>
          </TableToolbar>

          {isLoading ? (
            <>
              <EmptyState>
                <SpinWrapper>
                  <Loader2 />
                </SpinWrapper>
                Loading client listing...
              </EmptyState>
            </>
          ) : isError ? (
            <EmptyState>
              Failed to load clients. Please try again later.
              <RetryButton type="button" onClick={() => refetch()} disabled={isFetching}>
                <RotateCcw />
                {isFetching ? 'Retrying…' : 'Retry'}
              </RetryButton>
            </EmptyState>
          ) : (
            <Table
              data={filteredClients}
              columns={columns}
              hasFooter
              page={page - 1}
              pageSize={PAGE_SIZE}
              total={total}
              isLoading={isLoading}
              isFetching={isFetching}
              prevPage={() => setPage((p) => Math.max(p - 1, 1))}
              nextPage={() => setPage((p) => p + 1)}
              emptyStateText="No clients match your search"
            />
          )}
        </TableCard>
      </TeamMembersContainer>

      <CreateAndUpdateClientModal
        isOpen={isClientModalOpen}
        onClose={handleCloseClientModal}
        editingClient={editingClient}
      />

      <InviteClientModal
        isOpen={!!inviteTarget}
        onClose={handleCloseInvite}
        clientId={inviteTarget?.clientId}
        clientName={inviteTarget?.name}
      />
    </TeamMembersWrapper>
  );
};

export default Clients;
